import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { movimientosPredefinidosAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import '../Clientes.css';

const MovimientosPredefinidos = () => {
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroUso, setFiltroUso] = useState('');
  const { hasPermission } = useAuth();

  const loadMovimientos = useCallback(async () => {
    try {
      setLoading(true);
      const params = { activo: true };
      if (filtroTipo) params.tipo_movimiento = filtroTipo;
      if (filtroUso) params.uso = filtroUso;
      const response = await movimientosPredefinidosAPI.getAll(params);
      if (response.success) {
        setMovimientos(response.data);
      }
    } catch (error) {
      toast.error('Error al cargar movimientos predefinidos');
    } finally {
      setLoading(false);
    }
  }, [filtroTipo, filtroUso]);

  useEffect(() => {
    loadMovimientos();
  }, [loadMovimientos]);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este movimiento predefinido?')) return;

    try {
      const response = await movimientosPredefinidosAPI.delete(id);
      if (response.success) {
        toast.success('Movimiento eliminado exitosamente');
        loadMovimientos();
      }
    } catch (error) {
      toast.error(error.message || 'Error al eliminar movimiento');
    }
  };

  const esMovimientoSistema = (mov) => {
    const desc = (mov.descripcion || '').toLowerCase();
    if (desc.includes('abono mensual')) return true;
    if (desc === 'recibo' || desc.includes('recibo de cobro')) return true;
    if (desc.includes('orden de pago')) return true;
    if ((mov.uso || '').toLowerCase() === 'fondos') return true;
    return false;
  };

  const labelUso = (uso) => (uso === 'proveedores' ? 'Proveedores' : (uso === 'fondos' ? 'Fondos' : 'Clientes'));

  const labelTipo = (mov) => {
    if (mov.uso === 'fondos') return '—';
    if (mov.uso === 'proveedores') return mov.tipo_proveedor_nombre || 'Todos';
    return mov.tipo_cliente_nombre || 'Todos';
  };

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="clientes pagina-listado pagina-listado--scroll">
      <div className="listado-panel clientes-listado listado-controles">
        <div className="filters" style={{ marginBottom: 0 }}>
        {hasPermission('crear_debitos') && (
          <Link to="/movimientos-predefinidos/nuevo" className="btn-primary" style={{ marginRight: '10px' }}>
            Nuevo Movimiento
          </Link>
        )}
        <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
          <option value="">Todos los tipos</option>
          <option value="DEBITO">Débitos</option>
          <option value="CREDITO">Créditos</option>
        </select>
        <select value={filtroUso} onChange={(e) => setFiltroUso(e.target.value)}>
          <option value="">Se aplica a: todos</option>
          <option value="clientes">Clientes</option>
          <option value="proveedores">Proveedores</option>
          <option value="fondos">Fondos</option>
        </select>
        </div>

        <div className="listado-panel-cuerpo clientes-listado-cuerpo">
          <div className="listado-tabla-area clientes-listado-tabla-area">
            <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Descripción</th>
              <th>Valor predeterminado</th>
              <th>Tipo</th>
              <th>Se aplica a</th>
              <th>Tipo</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {movimientos.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">No hay movimientos predefinidos registrados</td>
              </tr>
            ) : (
              movimientos.map(mov => (
                <tr key={mov.id}>
                  <td><strong>{mov.descripcion}</strong></td>
                  <td className={mov.tipo_movimiento === 'DEBITO' ? 'negative' : 'positive'}>
                    {mov.monto == null || mov.monto === ''
                      ? <span style={{ fontStyle: 'italic', color: '#666' }}>—</span>
                      : (mov.descripcion === 'Abono Mensual' && parseFloat(mov.monto) === 0
                        ? <span style={{ fontStyle: 'italic', color: '#666' }}>Según tipo de cliente</span>
                        : `$${parseFloat(mov.monto).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`)}
                  </td>
                  <td>
                    <span className={`badge ${mov.tipo_movimiento === 'DEBITO' ? 'debito' : 'credito'}`}>
                      {mov.tipo_movimiento}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${(mov.uso || 'clientes') === 'proveedores' ? 'debito' : 'credito'}`}>
                      {labelUso(mov.uso || 'clientes')}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      background: '#e8f4f8',
                      color: '#2c3e50'
                    }}>
                      {labelTipo(mov)}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${mov.activo ? 'activo' : 'inactivo'}`}>
                      {mov.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {esMovimientoSistema(mov) ? (
                        hasPermission('ver_movimientos') && (
                          <Link
                            to={`/movimientos-predefinidos/editar/${mov.id}`}
                            className="btn-action-icon"
                            title="Ver configuración del movimiento predefinido (sistema)"
                          >
                            <span aria-hidden="true">👁️</span>
                          </Link>
                        )
                      ) : (
                        hasPermission('crear_debitos') && (
                          <>
                            <Link
                              to={`/movimientos-predefinidos/editar/${mov.id}`}
                              className="btn-action-icon btn-action-edit"
                              title="Editar movimiento predefinido"
                            >
                              <span aria-hidden="true">✏️</span>
                            </Link>
                            <button
                              type="button"
                              onClick={() => handleDelete(mov.id)}
                              className="btn-action-icon"
                              title="Eliminar movimiento predefinido"
                            >
                              <span aria-hidden="true">🗑️</span>
                            </button>
                          </>
                        )
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovimientosPredefinidos;
